import { useFormikSubmit } from "@certn/api";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { TextField } from "@certn/cascadia-react/components/inputs/text-field/TextField";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Schemas } from "../../../../types";

interface Props {
  apiConfigurationID: string;
  isOpen: boolean;
  onCreated: () => void;
  onOpenChange: (isOpen: boolean) => void;
}

export function CreateAPIKeyModal({
  apiConfigurationID,
  onCreated,
  isOpen,
  onOpenChange,
}: Props) {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState<string | null>(null);

  useEffect(() => {
    setApiKey(null);
  }, [isOpen]);

  const { submit, isSubmitting } = useFormikSubmit<
    Schemas["PublicAPIKeyCreateResponse"]
  >("/api/internal/client-portal/public-api/api-keys/create/", {
    onSuccess: ({ api_key }) => {
      setApiKey(api_key);
      onCreated();
    },
    showToastOnError: true,
  });

  const copyApiKeyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(apiKey || "");
  }, [apiKey]);

  return (
    <Modal
      testID="create-api-key-modal"
      isDismissable={!apiKey}
      onOpenChange={onOpenChange}
      isOpen={isOpen}
    >
      <Dialog>
        {() =>
          apiKey ? (
            <>
              <DialogHeader onClose={() => onOpenChange(false)}>
                {t("Save your key")}
              </DialogHeader>
              <DialogContent>
                <Stack gap="space-4">
                  <Text style="global/body-primary">
                    <Trans>
                      Save this secret key somewhere safe. For security reasons,
                      you won't be able to view it again. If you lose this key,
                      you will be required to generate a new one.
                    </Trans>
                  </Text>

                  <Stack gap="space-3" orientation="horizontal" align="end">
                    <TextField
                      label={t("API key")}
                      testID="api-key"
                      value={apiKey}
                      isReadOnly
                      isRequired
                      isDisabled
                      className={styles.apiKeyField}
                    />
                    <BasicButton
                      size="medium"
                      testID="copy-api-key"
                      aria-label="Copy"
                      leftIcon="content_copy"
                      onPress={copyApiKeyToClipboard}
                    >
                      {t("Copy")}
                    </BasicButton>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogFooter>
                <PrimaryButton
                  onPress={() => onOpenChange(false)}
                  testID="close"
                >
                  {t("Close")}
                </PrimaryButton>
              </DialogFooter>
            </>
          ) : (
            <Formik<{ name?: string; public_api_configuration: string }>
              initialValues={{
                name: undefined,
                public_api_configuration: apiConfigurationID,
              }}
              onSubmit={submit}
            >
              <Form>
                <DialogHeader>{t("Create API key")}</DialogHeader>
                <DialogContent>
                  <TextInputFormikField
                    label={t("API key name")}
                    testID="api-key-name"
                    name="name"
                    isRequired
                  />
                </DialogContent>
                <DialogFooter>
                  <BasicButton
                    onPress={() => onOpenChange(false)}
                    testID="cancel"
                    isDisabled={isSubmitting}
                  >
                    {t("Cancel")}
                  </BasicButton>
                  <PrimaryButton
                    testID="generate-key"
                    type="submit"
                    isDisabled={isSubmitting}
                  >
                    {t("Generate key")}
                  </PrimaryButton>
                </DialogFooter>
              </Form>
            </Formik>
          )
        }
      </Dialog>
    </Modal>
  );
}

const styles = {
  apiKeyField: css({
    flexGrow: 1,
  }),
};
